import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../api/queryClient'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { TooltipProvider } from './ui/tooltip'
import { AppIcon } from './ui/icon'
import type { EntityLog } from '../api/schema'
import { ActivityEntry } from './activity/ActivityEntry'
import { EmptyState } from './ui/empty-state'
import { useDebounce } from '../hooks/useDebounce'
import { mdiLoading, mdiRefresh } from '@mdi/js'
import { SECTION_ICONS } from '../lib/section-icons'

interface AssetActivityLogProps {
  org: string
  assetUid: string
}

const PAGE_SIZE = 50

// Visibility filtering happens server-side via the entitylogs allowlist —
// the API only returns rows whose changed fields are user-relevant, with
// soft-delete UPDATEs already rewritten to DELETE / RESTORE. The frontend
// just renders what it gets.

const FILTER_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: 'all', labelKey: 'activity.filter_all' },
  { value: 'assets', labelKey: 'activity.filter_asset' },
  { value: 'asset_data', labelKey: 'activity.filter_properties' },
  { value: 'documents', labelKey: 'activity.filter_documents' },
  { value: 'contract_assets', labelKey: 'activity.filter_contracts' },
  { value: 'pages', labelKey: 'activity.filter_notes' },
  { value: 'tags_entities', labelKey: 'activity.filter_tags' },
  { value: 'issues', labelKey: 'activity.filter_issues' },
  { value: 'asset_frame_registrations', labelKey: 'activity.filter_plans' },
]

export function AssetActivityLog({ org, assetUid }: AssetActivityLogProps) {
  const { t, i18n } = useTranslation('assets')
  const [filter, setFilter] = useState<string>('all')
  // Free-text search (spec 2026-09-25-03-activity-feed-text-search.md),
  // debounced 300ms like the org-wide feed's own search field — "dynamic"
  // filtering means filter-as-you-type, not a submit button.
  const [qInput, setQInput] = useState<string>('')
  const debouncedQ = useDebounce(qInput, 300)
  const [offset, setOffset] = useState<number>(0)
  const [accumulated, setAccumulated] = useState<EntityLog[]>([])

  // Reset when a filter changes
  useEffect(() => {
    setOffset(0)
    setAccumulated([])
  }, [filter, debouncedQ])

  const query: Record<string, any> = { limit: PAGE_SIZE, offset }
  if (filter !== 'all') query.entity_table = filter
  if (debouncedQ) query.q = debouncedQ

  const { data, isLoading, isFetching, refetch } = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/assets/{assetUid}/logs' as any,
    {
      params: {
        path: { orgCode: org, assetUid },
        query,
      },
    },
  ) as {
    data: any
    isLoading: boolean
    isFetching: boolean
    refetch: () => Promise<{ data?: { items?: EntityLog[] } }>
  }

  const handleRefresh = async () => {
    // Don't clear accumulated up-front: if refetch returns cached/identical
    // data, the merge effect (which keys on `data` identity) won't refire and
    // the list would stay empty. Instead, await the refetch and overwrite
    // from its result.
    setOffset(0)
    const res = await refetch()
    if (res?.data?.items) {
      setAccumulated(res.data.items)
    }
  }

  const items: EntityLog[] = (data?.items as EntityLog[]) || []
  const total: number = (data?.total as number) || 0

  // Merge new items into accumulated list when page loads
  useEffect(() => {
    if (!data) return
    if (offset === 0) {
      setAccumulated(items)
    } else {
      setAccumulated(prev => {
        const seen = new Set(prev.map(i => i.uid))
        const newOnes = items.filter(i => !seen.has(i.uid))
        return newOnes.length > 0 ? [...prev, ...newOnes] : prev
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const displayItems = accumulated
  const hasMore = displayItems.length < total

  const locale = i18n.language || 'en'

  // Infinite scroll: when the sentinel near the bottom of the list scrolls
  // into view, auto-advance the offset. We re-observe whenever the dependency
  // values change so the latest `offset`/`isFetching`/`hasMore` are read.
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!hasMore) return
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (entry?.isIntersecting && !isFetching && hasMore) {
          setOffset(displayItems.length)
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, isFetching, displayItems.length])

  return (
    <div className="bg-card rounded-lg border border-border p-6" data-testid="activity-log">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <Input
          type="search"
          value={qInput}
          onChange={e => setQInput(e.target.value)}
          placeholder={t('activity.filter_search_placeholder')}
          className="w-[220px]"
          aria-label={t('activity.filter_search')}
          data-testid="activity-asset-filter-q"
        />
        <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={isFetching}
          data-testid="activity-refresh"
          aria-label={t('activity.refresh')}
          title={t('activity.refresh')}
        >
          <AppIcon path={mdiRefresh} size="sm" className={`${isFetching ? 'motion-safe:animate-spin' : ''}`} />
        </Button>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="activity-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTER_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value} data-testid={`activity-filter-${opt.value}`}>
                {t(opt.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        </div>
      </div>

      {isLoading && displayItems.length === 0 && (
        <div className="flex justify-center py-8">
          <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
        </div>
      )}

      {!isLoading && displayItems.length === 0 && (
        <EmptyState
          variant="inline"
          className="py-12"
          icon={SECTION_ICONS.activity}
          title={debouncedQ ? t('activity.empty_search') : t('activity.empty')}
          data-testid="activity-empty"
          live
        />
      )}

      {displayItems.length > 0 && (
        <TooltipProvider>
          <ul className="space-y-4">
            {displayItems.map(entry => (
              <ActivityEntry key={entry.uid} entry={entry} org={org} locale={locale} />
            ))}
          </ul>
        </TooltipProvider>
      )}

      {displayItems.length > 0 && (
        <div className="flex justify-center mt-4">
          {hasMore ? (
            <div
              ref={sentinelRef}
              className="flex items-center gap-2 py-2 text-xs text-muted-foreground"
              data-testid="activity-load-more"
            >
              <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
              {t('activity.load_more')}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground" data-testid="activity-no-more">
              {t('activity.no_more')}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
